import React, { useState } from 'react';

// Define types for our component
interface XmlToSqlProps {}

// Type for SQL Generation Options
interface SqlGenerationOptions {
  statementType: 'insert' | 'create-table' | 'both';
  tableName: string;
  includePrimaryKey: boolean;
  primaryKeyField: string;
  schemaName: string;
  stringMaxLength: number;
  generateDropStatement: boolean;
  inferDataTypes: boolean;
}

// Type for SQL Output
interface SqlOutput {
  createTable?: string;
  insert?: string;
  dropTable?: string;
}

// Helper to infer SQL data type from a value
const inferSqlDataType = (value: string, options: SqlGenerationOptions): string => {
  if (value === null || value === undefined || value.trim() === '') {
    return 'VARCHAR(255)';
  }

  // Try to parse as integer
  if (/^-?\d+$/.test(value)) {
    const num = parseInt(value, 10);
    if (num > 2147483647 || num < -2147483648) {
      return 'BIGINT';
    }
    return 'INT';
  }

  // Try to parse as decimal/float
  if (/^-?\d+\.\d+$/.test(value)) {
    return 'DECIMAL(18,6)';
  }

  // Try to parse as date (simple check for YYYY-MM-DD format)
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return 'DATE';
  }

  // Try to parse as datetime (simple check for ISO format)
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
    return 'DATETIME';
  }

  // Boolean check
  if (/^(true|false)$/i.test(value)) {
    return 'BOOLEAN';
  }

  // Default to VARCHAR with configurable max length
  return `VARCHAR(${options.stringMaxLength})`;
}

// XML to SQL conversion function
const xmlToSql = (xml: string, options: SqlGenerationOptions): SqlOutput => {
  try {
    // Create a DOM parser and parse the XML string
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml, "application/xml");
    
    // Check for parsing errors
    const parseError = xmlDoc.getElementsByTagName("parsererror");
    if (parseError.length > 0) {
      throw new Error("XML Parsing Error: " + parseError[0].textContent);
    }
    
    const result: SqlOutput = {};
    const rootElement = xmlDoc.documentElement;
    
    // Determine if the root element is a collection of records or a single record
    const isCollection = rootElement.children.length > 0 && 
                        Array.from(rootElement.children).every(child => child.nodeName === rootElement.children[0].nodeName);
    
    // The table name will be based on the child element name or the root element if there's only a single record
    const tableName = options.tableName || 
                    (isCollection ? rootElement.children[0].nodeName : rootElement.nodeName);
    
    // Get the qualified table name (with schema if provided)
    const qualifiedTableName = options.schemaName ? 
                            `${options.schemaName}.${tableName}` : 
                            tableName;
    
    // Determine the records to process
    const records = isCollection ? 
                  Array.from(rootElement.children) : 
                  [rootElement];
    
    if (records.length === 0) {
      throw new Error("No records found in the XML");
    }
    
    // Collect all field names and their data across all records
    const allFields = new Map<string, Set<string>>();
    
    records.forEach(record => {
      const processNode = (node: Element, prefix: string = '') => {
        // Process attributes
        Array.from(node.attributes).forEach(attr => {
          const fieldName = prefix ? `${prefix}_${attr.name}` : attr.name;
          if (!allFields.has(fieldName)) {
            allFields.set(fieldName, new Set<string>());
          }
          allFields.get(fieldName)?.add(attr.value || '');
        });
        
        // Process child elements
        Array.from(node.children).forEach(child => {
          const childName = prefix ? `${prefix}_${child.nodeName}` : child.nodeName;
          
          // If child has children, process recursively
          if (child.children.length > 0) {
            processNode(child, childName);
          } else {
            // Leaf node with text content
            if (!allFields.has(childName)) {
              allFields.set(childName, new Set<string>());
            }
            allFields.get(childName)?.add(child.textContent || '');
          }
        });
        
        // Handle text content of the node itself if it has no children
        if (node.children.length === 0 && node.textContent && node.textContent.trim() !== '') {
          const fieldName = prefix || 'value';
          if (!allFields.has(fieldName)) {
            allFields.set(fieldName, new Set<string>());
          }
          allFields.get(fieldName)?.add(node.textContent.trim());
        }
      };
      
      processNode(record as Element);
    });
    
    // Convert the field Sets to arrays
    const fieldMap = new Map<string, string[]>();
    allFields.forEach((values, fieldName) => {
      fieldMap.set(fieldName, Array.from(values));
    });
    
    // Generate CREATE TABLE statement if requested
    if (options.statementType === 'create-table' || options.statementType === 'both') {
      let createTableSql = `CREATE TABLE ${qualifiedTableName} (\n`;
      
      // Add fields
      const fieldDefinitions: string[] = [];
      
      fieldMap.forEach((values, fieldName) => {
        let dataType: string;
        
        if (options.inferDataTypes) {
          // Use the first non-empty value to infer data type
          const sampleValue = values.find(v => v !== null && v !== undefined && v.trim() !== '') || '';
          dataType = inferSqlDataType(sampleValue, options);
        } else {
          // Default all to VARCHAR
          dataType = `VARCHAR(${options.stringMaxLength})`;
        }
        
        const isPrimaryKey = options.includePrimaryKey && fieldName === options.primaryKeyField;
        
        fieldDefinitions.push(`  ${fieldName} ${dataType}${isPrimaryKey ? ' PRIMARY KEY' : ''}`);
      });
      
      // If no primary key field was found among existing fields and includePrimaryKey is true
      if (options.includePrimaryKey && !fieldMap.has(options.primaryKeyField)) {
        fieldDefinitions.unshift(`  ${options.primaryKeyField} INT PRIMARY KEY`);
      }
      
      createTableSql += fieldDefinitions.join(',\n');
      createTableSql += '\n);';
      
      result.createTable = createTableSql;
    }
    
    // Generate DROP TABLE statement if requested
    if (options.generateDropStatement) {
      result.dropTable = `DROP TABLE IF EXISTS ${qualifiedTableName};`;
    }
    
    // Generate INSERT statements if requested
    if (options.statementType === 'insert' || options.statementType === 'both') {
      // Get all field names
      const fieldNames = Array.from(fieldMap.keys());
      
      // Generate INSERT statements for each record
      const insertStatements: string[] = [];
      
      records.forEach((record, index) => {
        const values: {[key: string]: string} = {};
        
        const extractValues = (node: Element, prefix: string = '') => {
          // Extract attributes
          Array.from(node.attributes).forEach(attr => {
            const fieldName = prefix ? `${prefix}_${attr.name}` : attr.name;
            values[fieldName] = attr.value || '';
          });
          
          // Extract child elements
          Array.from(node.children).forEach(child => {
            const childName = prefix ? `${prefix}_${child.nodeName}` : child.nodeName;
            
            // If child has children, process recursively
            if (child.children.length > 0) {
              extractValues(child, childName);
            } else {
              // Leaf node with text content
              values[childName] = child.textContent || '';
            }
          });
          
          // Handle text content of the node itself if it has no children
          if (node.children.length === 0 && node.textContent && node.textContent.trim() !== '') {
            const fieldName = prefix || 'value';
            values[fieldName] = node.textContent.trim();
          }
        };
        
        extractValues(record as Element);
        
        // Generate the INSERT statement
        let insertSql = `INSERT INTO ${qualifiedTableName} (`;
        insertSql += fieldNames.join(', ');
        insertSql += ') VALUES (';
        
        const valueParts: string[] = [];
        fieldNames.forEach(fieldName => {
          const value = values[fieldName] || null;
          if (value === null) {
            valueParts.push('NULL');
          } else {
            // Escape single quotes in the value
            const escapedValue = value.replace(/'/g, "''");
            valueParts.push(`'${escapedValue}'`);
          }
        });
        
        insertSql += valueParts.join(', ');
        insertSql += ');';
        
        insertStatements.push(insertSql);
      });
      
      result.insert = insertStatements.join('\n');
    }
    
    return result;
  } catch (error) {
    throw error;
  }
};

// Main component to convert XML to SQL
const XmlToSql: React.FC<XmlToSqlProps> = () => {
  // State for the XML input
  const [xmlInput, setXmlInput] = useState<string>('');
  
  // State for SQL output
  const [sqlOutput, setSqlOutput] = useState<string>('');
  
  // State for error messages
  const [error, setError] = useState<string>('');
  
  // State for the file name
  const [fileName, setFileName] = useState<string>('xml_data');
  
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  
  // State to track if the conversion happened
  const [isSqlGenerated, setIsSqlGenerated] = useState<boolean>(false);
  
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  
  // State for SQL generation options
  const [options, setOptions] = useState<SqlGenerationOptions>({
    statementType: 'both',
    tableName: '',
    includePrimaryKey: true,
    primaryKeyField: 'id',
    schemaName: '',
    stringMaxLength: 255,
    generateDropStatement: true,
    inferDataTypes: true
  });

  // Handle XML input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setXmlInput(e.target.value);
    setError('');
    setIsSqlGenerated(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Handle option changes
  const handleOptionChange = (field: keyof SqlGenerationOptions, value: any) => {
    setOptions(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Use example data
  const useExampleData = () => {
    const exampleXml = `<?xml version="1.0" encoding="UTF-8"?>
<employees>
  <employee id="1" department="Engineering">
    <first_name>John</first_name>
    <last_name>Smith</last_name>
    <email>john.smith@example.com</email>
    <salary>75000</salary>
    <hire_date>2020-06-15</hire_date>
    <is_manager>true</is_manager>
  </employee>
  <employee id="2" department="Marketing">
    <first_name>Jane</first_name>
    <last_name>Doe</last_name>
    <email>jane.doe@example.com</email>
    <salary>82500.50</salary>
    <hire_date>2019-03-24</hire_date>
    <is_manager>false</is_manager>
  </employee>
  <employee id="3" department="Engineering">
    <first_name>Michael</first_name>
    <last_name>Johnson</last_name>
    <email>michael.johnson@example.com</email>
    <salary>68000</salary>
    <hire_date>2021-11-08</hire_date>
    <is_manager>false</is_manager>
  </employee>
</employees>`;

    setXmlInput(exampleXml);
    setError('');
    setIsSqlGenerated(false);
    setSqlOutput('');
    setOptions(prev => ({
      ...prev,
      tableName: 'employee'
    }));
  };

  // Format/Beautify XML
  const formatXml = () => {
    try {
      if (!xmlInput.trim()) {
        setError('Please enter XML data to format');
        return;
      }
      
      // Parse the XML
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlInput, "application/xml");
      
      // Check for parsing errors
      const parseError = xmlDoc.getElementsByTagName("parsererror");
      if (parseError.length > 0) {
        throw new Error("XML Parsing Error: " + parseError[0].textContent);
      }
      
      // Serialize back to string with pretty formatting
      const serializer = new XMLSerializer();
      let formattedXml = '';
      
      // Function to pretty print XML with indentation
      const prettyPrintXml = (node: Node, indent: string = ''): string => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.nodeValue?.trim();
          return text ? indent + text + '\n' : '';
        }
        
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          let result = indent + '<' + element.nodeName;
          
          // Add attributes
          for (let i = 0; i < element.attributes.length; i++) {
            const attr = element.attributes[i];
            result += ` ${attr.nodeName}="${attr.nodeValue}"`;
          }
          
          // Check if there are any child nodes
          if (element.childNodes.length === 0) {
            result += '/>\n';
            return result;
          }
          
          result += '>\n';
          
          // Process child nodes
          for (let i = 0; i < element.childNodes.length; i++) {
            result += prettyPrintXml(element.childNodes[i], indent + '  ');
          }
          
          result += indent + '</' + element.nodeName + '>\n';
          return result;
        }
        
        return '';
      };
      
      // Add XML declaration
      formattedXml += '<?xml version="1.0" encoding="UTF-8"?>\n';
      
      // Format the document element
      formattedXml += prettyPrintXml(xmlDoc.documentElement);
      
      setXmlInput(formattedXml);
      setError('');
    } catch (e) {
      setError(`Error formatting XML: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Convert XML to SQL
  const convertXmlToSql = () => {
    setError('');
    setIsLoading(true);
    setIsSqlGenerated(false);
    setSqlOutput('');
    
    if (!xmlInput.trim()) {
      setError('Please enter XML data');
      setIsLoading(false);
      return;
    }

    try {
      // Convert XML to SQL
      const sqlData = xmlToSql(xmlInput, options);
      
      // Format SQL output
      let sql = '';
      
      if (options.generateDropStatement && sqlData.dropTable) {
        sql += sqlData.dropTable + '\n\n';
      }
      
      if (sqlData.createTable) {
        sql += sqlData.createTable + '\n\n';
      }
      
      if (sqlData.insert) {
        sql += sqlData.insert;
      }
      
      setSqlOutput(sql);
      setIsSqlGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error converting XML to SQL: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setXmlInput('');
    setSqlOutput('');
    setError('');
    setFileName('xml_data');
    setIsSqlGenerated(false);
    setOptions({
      statementType: 'both',
      tableName: '',
      includePrimaryKey: true,
      primaryKeyField: 'id',
      schemaName: '',
      stringMaxLength: 255,
      generateDropStatement: true,
      inferDataTypes: true
    });
  };

  // Copy SQL to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(sqlOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download SQL file
  const downloadSql = () => {
    const blob = new Blob([sqlOutput], { type: 'application/sql;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'xml_data'}.sql`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">XML to SQL Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert XML data to SQL statements. Paste your XML data below to generate SQL CREATE TABLE and INSERT statements.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="xmlInput" className="font-medium text-gray-700">
                XML Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={formatXml}
                >
                  Format XML
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setXmlInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="xmlInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={xmlInput}
              onChange={handleInputChange}
              placeholder="Paste your XML data here, e.g.,
<?xml version='1.0' encoding='UTF-8'?>
<employees>
  <employee id='1'>
    <name>John Smith</name>
    <department>Engineering</department>
  </employee>
  <employee id='2'>
    <name>Jane Doe</name>
    <department>Marketing</department>
  </employee>
</employees>"
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="tableName" className="block text-sm font-medium text-gray-700 mb-1">
                Table Name (optional)
              </label>
              <input
                type="text"
                id="tableName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={options.tableName}
                onChange={(e) => handleOptionChange('tableName', e.target.value)}
                placeholder="Leave empty to auto-detect from XML"
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="statementType" className="block text-sm font-medium text-gray-700 mb-1">
                SQL Statement Type
              </label>
              <select
                id="statementType"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={options.statementType}
                onChange={(e) => handleOptionChange('statementType', e.target.value)}
              >
                <option value="both">Both CREATE TABLE and INSERT</option>
                <option value="create-table">CREATE TABLE only</option>
                <option value="insert">INSERT only</option>
              </select>
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="generateDropStatement"
                    checked={options.generateDropStatement}
                    onChange={(e) => handleOptionChange('generateDropStatement', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="generateDropStatement" className="ml-2 block text-sm text-gray-700">
                    Generate DROP TABLE Statement
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="inferDataTypes"
                    checked={options.inferDataTypes}
                    onChange={(e) => handleOptionChange('inferDataTypes', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="inferDataTypes" className="ml-2 block text-sm text-gray-700">
                    Auto-detect SQL Data Types
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includePrimaryKey"
                    checked={options.includePrimaryKey}
                    onChange={(e) => handleOptionChange('includePrimaryKey', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="includePrimaryKey" className="ml-2 block text-sm text-gray-700">
                    Include Primary Key
                  </label>
                </div>
                
                {options.includePrimaryKey && (
                  <div>
                    <label htmlFor="primaryKeyField" className="block text-sm font-medium text-gray-700 mb-1">
                      Primary Key Field Name
                    </label>
                    <input
                      type="text"
                      id="primaryKeyField"
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      value={options.primaryKeyField}
                      onChange={(e) => handleOptionChange('primaryKeyField', e.target.value)}
                    />
                  </div>
                )}
                
                <div>
                  <label htmlFor="schemaName" className="block text-sm font-medium text-gray-700 mb-1">
                    Schema Name (optional)
                  </label>
                  <input
                    type="text"
                    id="schemaName"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={options.schemaName}
                    onChange={(e) => handleOptionChange('schemaName', e.target.value)}
                    placeholder="e.g. dbo"
                  />
                </div>
                
                <div>
                  <label htmlFor="stringMaxLength" className="block text-sm font-medium text-gray-700 mb-1">
                    Max Length for Strings
                  </label>
                  <input
                    type="number"
                    id="stringMaxLength"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={options.stringMaxLength}
                    onChange={(e) => handleOptionChange('stringMaxLength', parseInt(e.target.value, 10))}
                    min="1"
                    max="8000"
                  />
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertXmlToSql}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to SQL'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              SQL Output
            </label>
            
            {isSqlGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy SQL'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadSql}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isSqlGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    SQL output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isSqlGenerated && (
              <div className="flex-grow flex flex-col">                
                <div className="flex-grow flex flex-col">
                  <textarea
                    id="sqlOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={sqlOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isSqlGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">XML Successfully Converted to SQL!</p>
              </div>
              <p className="text-sm">
                You can now copy or download the generated SQL statements for use in your database.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About XML to SQL Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool transforms your XML data into SQL statements. It analyzes the structure of your XML document
              and generates appropriate SQL CREATE TABLE and INSERT statements, preserving the hierarchy and relationships
              in your data. The tool can intelligently infer SQL data types from your XML values.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>CREATE TABLE Generation</strong> - Automatically creates table definitions based on XML structure</li>
              <li><strong>INSERT Statement Generation</strong> - Converts XML elements to SQL INSERT statements</li>
              <li><strong>Data Type Inference</strong> - Intelligently detects appropriate SQL data types from XML values</li>
              <li><strong>Primary Key Support</strong> - Option to include primary key definition in table creation</li>
              <li><strong>Schema Support</strong> - Optional schema name for the generated SQL statements</li>
              <li><strong>Error Handling</strong> - Clear error messages for invalid XML input</li>
              <li><strong>DROP TABLE Generation</strong> - Option to include DROP TABLE statement before CREATE TABLE</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">XML to SQL Conversion Rules</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>XML element names become table names or column names depending on the structure</li>
              <li>XML attributes become columns in the database table</li>
              <li>Text content of XML elements becomes the values in INSERT statements</li>
              <li>Nested XML elements are flattened with parent element names as prefixes</li>
              <li>Numeric values are converted to appropriate number types (INT, DECIMAL, etc.)</li>
              <li>Date-like strings are converted to DATE or DATETIME types</li>
              <li>Boolean values ("true"/"false") are converted to BOOLEAN type</li>
              <li>Other text content is stored as VARCHAR with configurable max length</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format XML" button to check if your XML is valid before conversion</li>
              <li>Set an appropriate table name that follows SQL naming conventions</li>
              <li>For nested structures, consider how the flattened representation will look in SQL</li>
              <li>Use the "Auto-detect SQL Data Types" option for more appropriate column definitions</li>
              <li>Review the generated SQL before executing it in your database system</li>
              <li>Some database systems may require modifications to the generated SQL</li>
              <li>Use the example button to see a properly formatted XML example</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Supported Database Systems</h3>
            <p className="text-gray-600">
              The generated SQL statements are designed to be compatible with most major SQL database systems,
              including MySQL, PostgreSQL, SQL Server, and SQLite. However, some database-specific features
              or syntax may require manual adjustments.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default XmlToSql;